import type { IconName } from '@sparklayer-io/spark-dash-ui/components/ui/icon/types';
import type { ActivePlanKey } from '../../../constants/features';
import type { Platform } from '../../../context/env-context';
import type { PathId } from '../../../types/handlers/navigation';

/**
 * Host agnostic catalog for the Explore features page
 */

export type FeatureCategoryId = 'basics' | 'advanced' | 'extras';

type FeatureId =
  | 'storefront-widgets'
  | 'price-lists'
  | 'assigned-pricing'
  | 'order-rules'
  | 'stock-display'
  | 'payment-methods'
  | 'b2b-catalogs'
  | 'forms'
  | 'sales-agents'
  | 'net-terms'
  | 'product-ordering-rules'
  | 'advanced-discounts'
  | 'b2b-shipping'
  | 'invoicing'
  | 'connect-accounting'
  | 'quoting'
  | 'company-users';

/**
 * Example tiles that can be shown in a feature's expanded body
 */
export type FeatureExampleId =
  // Price lists
  | 'automatic'
  | 'manual'
  | 'api'
  | 'promotional'
  // Storefront widgets
  | 'product-detail'
  | 'product-cards'
  | 'cart'
  | 'account'
  // Assigned pricing
  | 'assigned-customer-specific'
  | 'assigned-collection'
  // Order rules
  | 'order-rules-value'
  | 'order-rules-quantity'
  // Stock display
  | 'stock-per-group'
  | 'stock-preorder'
  // Payment methods
  | 'payment-upfront'
  | 'payment-invoice'
  | 'payment-account'
  | 'payment-quote'
  // B2B catalogs
  | 'catalog-visibility'
  | 'catalog-exclusive'
  | 'catalog-per-group'
  | 'catalog-variants'
  // Forms
  | 'forms-application'
  | 'forms-approval'
  // Sales agents
  | 'agents-ordering'
  | 'agents-roles'
  // Net terms & credit
  | 'terms-net'
  | 'terms-credit'
  // Product ordering rules
  | 'rules-pack-size'
  | 'rules-moq'
  | 'rules-max-qty'
  | 'rules-price-breaks'
  // Advanced discounts
  | 'discounts-order'
  | 'discounts-codes'
  | 'discounts-group'
  | 'discounts-limits'
  // B2B shipping
  | 'shipping-zones'
  | 'shipping-weight'
  | 'shipping-group'
  | 'shipping-free'
  // Invoicing
  | 'invoicing-branded'
  | 'invoicing-drafts'
  // Connect accounting
  | 'accounting-invoices'
  | 'accounting-payments'
  // Quoting
  | 'quoting-request'
  | 'quoting-approve'
  // Company users
  | 'company-buyers'
  | 'company-approval';

/** Icon shown on each example's IconCard */
export const EXAMPLE_ICONS: Record<FeatureExampleId, IconName> = {
  // Price lists
  automatic: 'refresh',
  manual: 'upload',
  api: 'link',
  promotional: 'tag',
  // Storefront widgets
  'product-detail': 'cube',
  'product-cards': 'viewColumns',
  cart: 'cart',
  account: 'user',
  // Assigned pricing
  'assigned-customer-specific': 'userGroup',
  'assigned-collection': 'priceList',
  // Order rules
  'order-rules-value': 'payment',
  'order-rules-quantity': 'calculator',
  // Stock display
  'stock-per-group': 'userGroup',
  'stock-preorder': 'clock',
  // Payment methods
  'payment-upfront': 'payment',
  'payment-invoice': 'document',
  'payment-account': 'calendar',
  'payment-quote': 'chat',
  // B2B catalogs
  'catalog-visibility': 'view',
  'catalog-exclusive': 'premium',
  'catalog-per-group': 'userGroup',
  'catalog-variants': 'cube',
  // Forms
  'forms-application': 'document',
  'forms-approval': 'checkCircle',
  // Sales agents
  'agents-ordering': 'cart',
  'agents-roles': 'key',
  // Net terms & credit
  'terms-net': 'calendar',
  'terms-credit': 'payment',
  // Product ordering rules
  'rules-pack-size': 'cube',
  'rules-moq': 'plusCircle',
  'rules-max-qty': 'minusCircle',
  'rules-price-breaks': 'calculator',
  // Advanced discounts
  'discounts-order': 'cart',
  'discounts-codes': 'tag',
  'discounts-group': 'userGroup',
  'discounts-limits': 'clock',
  // B2B shipping
  'shipping-zones': 'mapPin',
  'shipping-weight': 'calculator',
  'shipping-group': 'userGroup',
  'shipping-free': 'shipping',
  // Invoicing
  'invoicing-branded': 'document',
  'invoicing-drafts': 'edit',
  // Connect accounting
  'accounting-invoices': 'document',
  'accounting-payments': 'payment',
  // Quoting
  'quoting-request': 'document',
  'quoting-approve': 'checkCircle',
  // Company users
  'company-buyers': 'userGroup',
  'company-approval': 'checkCircle',
};

export type FeatureCategory = {
  id: FeatureCategoryId;
};

export type CatalogFeature = {
  id: FeatureId;
  category: FeatureCategoryId;
  icon: IconName;
  /** Read the docs link */
  docsUrl?: string;
  image?: string;
  examples?: readonly FeatureExampleId[];
  minPlan?: ActivePlanKey;
  /** Restrict visibility to these platforms */
  availablePlatforms?: Platform[];
  /**
   * Primary CTA, where:
   * - `{ id }`: a {@link PathId} the host resolves per platform - use when the destination differs
   *   by the store's platform (e.g. setup page lives in the Shopify / Wix admin).
   * - `{ path }`: a host-local route - each host routes to its own page there.
   * - `{ path, openInDashboard: true }`: a dashboard only page - Shopify redirects out to it.
   * - `{ path, external: true }`: `path` is an absolute URL opened in a new tab (e.g. an app listing).
   */
  primaryLink: { id: PathId } | { path: string; openInDashboard?: boolean; external?: boolean };
};

export const FEATURE_CATEGORIES: readonly FeatureCategory[] = [
  { id: 'basics' },
  { id: 'advanced' },
  { id: 'extras' },
];

/** Lookup for the currently selected category */
export const FEATURE_CATEGORY_BY_ID = Object.fromEntries(
  FEATURE_CATEGORIES.map((category) => [category.id, category]),
) as Record<FeatureCategoryId, FeatureCategory>;

const SHOPIFY_PLATFORMS: Platform[] = ['shopify', 'shopify_plus'];

export const FEATURES_CATALOG: readonly CatalogFeature[] = [
  // --- Basics ---
  {
    id: 'storefront-widgets',
    category: 'basics',
    icon: 'store',
    docsUrl: 'https://docs.sparklayer.io/frontend-interfaces',
    examples: ['product-detail', 'product-cards', 'cart', 'account'],
    primaryLink: { id: 'onboarding.setup' },
  },
  {
    id: 'price-lists',
    category: 'basics',
    icon: 'priceList',
    docsUrl: 'https://docs.sparklayer.io/price-lists',
    examples: ['automatic', 'manual', 'api', 'promotional'],
    primaryLink: { path: '/pricing/lists' },
  },
  {
    id: 'assigned-pricing',
    category: 'basics',
    icon: 'priceList',
    docsUrl: 'https://docs.sparklayer.io/price-lists',
    examples: ['assigned-customer-specific', 'assigned-collection'],
    primaryLink: { path: '/pricing/lists' },
  },
  {
    id: 'order-rules',
    category: 'basics',
    icon: 'calculator',
    docsUrl: 'https://docs.sparklayer.io/customers#groups',
    examples: ['order-rules-value', 'order-rules-quantity'],
    primaryLink: { path: '/customers/groups' },
  },
  {
    id: 'stock-display',
    category: 'basics',
    icon: 'view',
    docsUrl: 'https://docs.sparklayer.io/stock-display',
    examples: ['stock-per-group', 'stock-preorder'],
    primaryLink: { path: '/customers/groups' },
  },
  {
    id: 'payment-methods',
    category: 'basics',
    icon: 'payment',
    docsUrl: 'https://docs.sparklayer.io/my-order-interface#payment-methods',
    examples: ['payment-upfront', 'payment-invoice', 'payment-account', 'payment-quote'],
    primaryLink: { path: '/customers/groups' },
  },
  {
    id: 'product-ordering-rules',
    category: 'basics',
    icon: 'cube',
    docsUrl: 'https://docs.sparklayer.io/quantity-pricing',
    examples: ['rules-pack-size', 'rules-moq', 'rules-max-qty', 'rules-price-breaks'],
    primaryLink: { path: '/integrations/platform', openInDashboard: true },
  },

  // --- Advanced ---
  {
    id: 'b2b-catalogs',
    category: 'advanced',
    icon: 'view',
    availablePlatforms: SHOPIFY_PLATFORMS,
    examples: ['catalog-visibility', 'catalog-exclusive', 'catalog-per-group', 'catalog-variants'],
    primaryLink: { path: 'https://apps.shopify.com/sparklayer-b2b-catalogs', external: true },
  },
  {
    id: 'forms',
    category: 'advanced',
    icon: 'document',
    docsUrl: 'https://docs.sparklayer.io/forms',
    examples: ['forms-application', 'forms-approval'],
    primaryLink: { path: '/forms', openInDashboard: true },
  },
  {
    id: 'sales-agents',
    category: 'advanced',
    icon: 'user',
    docsUrl: 'https://docs.sparklayer.io/sales-agent-ordering',
    examples: ['agents-ordering', 'agents-roles'],
    primaryLink: { path: '/sales-agents', openInDashboard: true },
  },
  {
    id: 'net-terms',
    category: 'advanced',
    icon: 'payment',
    docsUrl: 'https://docs.sparklayer.io/credit-net-terms-and-invoicing',
    examples: ['terms-net', 'terms-credit'],
    primaryLink: { path: '/configuration/general', openInDashboard: true },
  },
  {
    id: 'advanced-discounts',
    category: 'advanced',
    icon: 'tag',
    docsUrl: 'https://docs.sparklayer.io/discounts',
    examples: ['discounts-order', 'discounts-codes', 'discounts-group', 'discounts-limits'],
    primaryLink: { path: '/pricing/discounts', openInDashboard: true },
  },

  // --- Extras ---
  {
    id: 'b2b-shipping',
    category: 'extras',
    icon: 'shipping',
    docsUrl: 'https://docs.sparklayer.io/shipping',
    examples: ['shipping-zones', 'shipping-weight', 'shipping-group', 'shipping-free'],
    primaryLink: { path: '/configuration/shipping', openInDashboard: true },
  },
  {
    id: 'invoicing',
    category: 'extras',
    icon: 'document',
    docsUrl: 'https://docs.sparklayer.io/invoices',
    examples: ['invoicing-branded', 'invoicing-drafts'],
    minPlan: 'v2_growth',
    primaryLink: { path: '/configuration/general', openInDashboard: true },
  },
  {
    id: 'connect-accounting',
    category: 'extras',
    icon: 'payment',
    docsUrl: 'https://docs.sparklayer.io/accountancy',
    examples: ['accounting-invoices', 'accounting-payments'],
    primaryLink: { path: '/integrations/partners', openInDashboard: true },
    minPlan: 'v2_pro',
  },
  {
    id: 'quoting',
    category: 'extras',
    icon: 'chat',
    docsUrl: 'https://docs.sparklayer.io/quoting',
    examples: ['quoting-request', 'quoting-approve'],
    minPlan: 'v2_pro',
    primaryLink: { path: '/configuration/general', openInDashboard: true },
  },
  {
    id: 'company-users',
    category: 'extras',
    icon: 'user',
    docsUrl: 'https://docs.sparklayer.io/company-users',
    examples: ['company-buyers', 'company-approval'],
    primaryLink: { path: '/v2/customers/list', openInDashboard: true },
  },
];
